import { call, put, select } from 'redux-saga/effects'
import { ApiResponse } from 'apisauce'
import api from 'src/services/api'
import { ApplicationState } from 'src/core/store'
import { ClientNotifyCatch } from 'src/config/bugsnag'
import findBasicApiResponseErrors from 'src/services/findBasicApiResponseErrors'
import i18n from 'src/config/i18n'
import objectToQueryString from 'src/helpers/object-to-query-string'
import { ResponseDataRequests, ResponseDataRequestsItem } from './types'
import * as Actions from './actions'

function noRequestEmissionFilter(requests: ResponseDataRequestsItem): boolean {
  return !!requests?.request?.request_id
}

export function* getIssuances({
  payload: { options }
}: ReturnType<typeof Actions.getIssuances>) {
  const {
    issuances,
    me: { organization }
  } = yield select((state: ApplicationState) => state)

  let params: any = {
    page_size: 10,
    page: options.page || 1
  }

  const {
    filterOrganizationsId,
    filterStatus,
    filterTenantId,
    request_agency_ids,
    request_ids,
    request_numbers,
    request_title
  } = options

  if (request_agency_ids?.length) {
    params.request_agency_ids = request_agency_ids.join(',')
  }

  if (request_ids?.length) {
    params.request_ids = request_ids.join(',')
  }

  const requestNumbers = request_numbers ?? issuances.filterRequestNumber
  const requestTitle = request_title ?? issuances.filterRequestTitle
  const filter_status = filterStatus ?? issuances.filterStatus
  const customer_organization_id =
    filterOrganizationsId ?? issuances.filterOrganizationsId
  const customer_tenant_id = filterTenantId ?? issuances.filterTenantId

  if (requestNumbers) {
    params.request_numbers = requestNumbers
  }

  if (requestTitle) {
    params.title = requestTitle
  }

  if (customer_tenant_id) {
    params.customer_tenant_id = customer_tenant_id
    params.customer_organization_id = customer_organization_id
  }

  const queryString = objectToQueryString(
    Object.assign(
      params,
      ...filter_status.map((status: string) => ({
        [status]: 'TRUE'
      }))
    )
  )

  try {
    const response: ApiResponse<ResponseDataRequests> = yield call(
      api.get,
      `/organizations/${organization.organization_id}/request_agencies${queryString}`
    )

    if (response.ok) {
      const {
        organizations = [],
        request_totals = {
          total_emissions: 0,
          total_quotations: 0
        },
        requests = [],
        total_results = 0
      } = response.data

      yield put(
        Actions.getIssuancesSuccess({
          organizations,
          request_totals,
          requests: requests.filter(noRequestEmissionFilter),
          total_results
        })
      )
      return
    }

    const message = findBasicApiResponseErrors(response)
    yield put(Actions.getIssuancesFailure(message))
  } catch (err) {
    ClientNotifyCatch('Saga getIssuances', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(Actions.getIssuancesFailure(message))
  }
}

export function* sendIssuance({
  payload: { request_agency_emissions }
}: ReturnType<typeof Actions.sendIssuance>) {
  try {
    const {
      issuances,
      me: { user }
    } = yield select((state: ApplicationState) => state)

    const responsePayload = {
      request_agency_emissions,
      request_ticket_air_selecteds: issuances.ticketsEmission
        .filter(
          (item: any) =>
            item.request_ticket_type === 'AIR' &&
            item.request_ticket.status === 'EMISSION_PENDING'
        )
        .map(({ request_ticket, ...item }: any) => ({
          ...item,
          request_ticket_air: request_ticket,
          status: item.pending ? 'ISSUED' : 'EMISSION_CANCELED'
        })),
      request_ticket_vehicle_selecteds: issuances.ticketsEmission
        .filter(
          (item: any) =>
            item.request_ticket_type === 'VEHICLE' &&
            item.request_ticket.status === 'EMISSION_PENDING'
        )
        .map(({ request_ticket, ...item }: any) => ({
          ...item,
          request_ticket_vehicle: request_ticket,
          status: item.pending ? 'ISSUED' : 'EMISSION_CANCELED'
        })),
      request_ticket_hotel_selecteds: issuances.ticketsEmission
        .filter(
          (item: any) =>
            item.request_ticket_type === 'HOTEL' &&
            item.request_ticket.status === 'EMISSION_PENDING'
        )
        .map(({ request_ticket, ...item }: any) => ({
          ...item,
          request_ticket_hotel: request_ticket,
          status: item.pending ? 'ISSUED' : 'EMISSION_CANCELED'
        }))
    }
    const response: ApiResponse<ResponseDataRequests> = yield call(
      api.post,
      `users/${user.user_id}/agencies/emissions/requests`,
      responsePayload
    )

    if (response.ok) {
      yield put(Actions.sendIssuanceSuccess())
      yield put(Actions.setTicketsEmission([]))
      return
    }

    const message = findBasicApiResponseErrors(response)
    yield put(Actions.sendIssuanceFailure(message))
  } catch (err) {
    ClientNotifyCatch('Saga sendIssuance', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(Actions.sendIssuanceFailure(message))
  }
}
